import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Chip, CoinBadge, EmptyState, Icon, Input, Modal, Skeleton, fmtCoins } from "../ui";
import { useAsync } from "../../hooks";
import api from "../../services/api";
import { errorInfo } from "../../services/errors";
import { MODEL_CATEGORIES } from "./format";

/** ข้อความ error ในกล่อง (คอยน์ไม่พอ -> ลิงก์ไปเติม) */
export function ErrorLine({ error, onNavigate }) {
  if (!error) return null;
  return (
    <div className="notice notice--error" role="alert">
      <span className="grow">{error.message}</span>
      {error.needCoins ? <Link to="/coins" className="btn btn--sm btn--primary" onClick={onNavigate}>เติมคอยน์</Link> : null}
    </div>
  );
}

export function ConfirmDialog({ open, title, children, confirmLabel = "ยืนยัน", danger, busy, onConfirm, onClose }) {
  return (
    <Modal open={open} title={title} size="sm" onClose={onClose}
      footer={<><Button onClick={onClose}>ยกเลิก</Button><Button variant={danger ? "danger" : "primary"} disabled={busy} onClick={onConfirm}>{confirmLabel}</Button></>}>
      {children}
    </Modal>
  );
}

export function RenameDialog({ open, name, onClose, onSave }) {
  const [v, setV] = useState(name || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setV(name || ""); }, [open, name]);
  const save = async () => {
    setBusy(true);
    try { await onSave(v.trim()); onClose(); } finally { setBusy(false); }
  };
  return (
    <Modal open={open} title="เปลี่ยนชื่อแชท" size="sm" onClose={onClose}
      footer={<><Button onClick={onClose}>ยกเลิก</Button><Button variant="primary" disabled={busy || !v.trim()} onClick={save}>บันทึก</Button></>}>
      <Input label="ชื่อแชท" maxLength={40} value={v} onChange={(e) => setV(e.target.value)} hint="ไม่เกิน 40 ตัวอักษร" autoFocus />
    </Modal>
  );
}

/** เลือกโมเดล AI — ราคา 2/5/10 คอยน์ต่อข้อความ แสดงชัดก่อนเลือก */
export function ModelDialog({ open, models, value, coins, onClose, onPick }) {
  const [cat, setCat] = useState("all");
  const rows = (models || []).filter((m) => cat === "all" || m.category === cat);
  return (
    <Modal open={open} title="เลือกโมเดล AI" onClose={onClose}>
      <p className="muted mb-8" style={{ fontSize: 13 }}>ราคาคิดต่อข้อความที่ส่ง · เริ่มต้น 2 คอยน์ (ประมาณ 14 สตางค์) · ถ้า AI ตอบไม่สำเร็จจะไม่หักคอยน์</p>
      <div className="chip-row mb-16" role="group" aria-label="หมวดโมเดล">
        {MODEL_CATEGORIES.map((c) => <Chip key={c.value} active={cat === c.value} onClick={() => setCat(c.value)}>{c.label}</Chip>)}
      </div>
      {!models ? <Skeleton height={80} /> : !rows.length ? <EmptyState icon="🤖" title="ไม่มีโมเดลในหมวดนี้" /> : (
        <div className="stack" role="radiogroup" aria-label="โมเดล AI">
          {rows.map((m) => (
            <button key={m.id} role="radio" aria-checked={m.id === value} className={`pick${m.id === value ? " is-active" : ""}`} onClick={() => onPick(m)}>
              <span className="grow">
                <span className="strong">{m.name}</span>
                {m.isNew ? <Chip size="sm" className="is-active" style={{ marginLeft: 6 }}>ใหม่</Chip> : null}
                {m.isDefault ? <Chip size="sm" style={{ marginLeft: 6 }}>แนะนำ</Chip> : null}
                <span className="muted" style={{ display: "block", fontSize: 13 }}>{m.description}</span>
              </span>
              <span className="pick__side"><CoinBadge amount={m.coinPerMessage} /><span className="faint" style={{ fontSize: 11 }}>ต่อข้อความ</span>
                {typeof coins === "number" && coins < m.coinPerMessage ? <span className="error-text" style={{ fontSize: 11 }}>คอยน์ไม่พอ</span> : null}</span>
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}

/** ส่งของขวัญ: เลือก -> เห็นยอดที่จะหัก -> ยืนยัน (หักคอยน์ทันทีที่ server) */
export function GiftDialog({ open, chatId, charName, coins, onClose, onSent }) {
  const gifts = useAsync(() => (open ? api.gifts.catalog() : Promise.resolve([])), [open]);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => { if (open) { setSel(null); setErr(null); } }, [open]);
  const enough = !sel || typeof coins !== "number" || coins >= sel.coinCost;
  const send = async () => {
    if (!sel || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await api.gifts.send(chatId, sel.id);
      onSent(r, sel);
      onClose();
    } catch (e) {
      setErr(errorInfo(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open={open} title="ส่งของขวัญ" onClose={onClose}
      footer={<>
        <span className="grow muted" style={{ fontSize: 13 }}>{sel ? <>หัก <b className="coin" style={{ display: "inline-flex" }}><i className="coin__dot" />{fmtCoins(sel.coinCost)}</b> · เหลือ {fmtCoins(Math.max(0, (coins || 0) - sel.coinCost))}</> : `คอยน์คงเหลือ ${fmtCoins(coins)}`}</span>
        <Button variant="primary" disabled={!sel || !enough || busy} onClick={send}>{busy ? "กำลังส่ง..." : sel ? `ส่ง${sel.name}` : "เลือกของขวัญ"}</Button>
      </>}>
      <p className="muted mb-16" style={{ fontSize: 13 }}>ส่งให้ {charName} — ตัวละครจะตอบขอบคุณ และส่วนหนึ่งของมูลค่าเป็นของครีเอเตอร์</p>
      {gifts.loading ? <Skeleton height={120} /> : gifts.error ? <div className="error-text">{gifts.error}</div> : (
        <div className="gift-grid">
          {(gifts.data || []).map((g) => (
            <button key={g.id} className={`gift${sel?.id === g.id ? " is-active" : ""}`} aria-pressed={sel?.id === g.id} onClick={() => { setSel(g); setErr(null); }}>
              <span className="gift__icon" aria-hidden="true">{g.emoji}</span>
              <span className="gift__name clamp-1">{g.name}</span>
              <CoinBadge amount={g.coinCost} />
            </button>
          ))}
        </div>
      )}
      {!enough ? <div className="notice notice--error mt-16" role="alert"><span className="grow">คอยน์ไม่พอ ต้องการ {fmtCoins(sel.coinCost)} แต่มี {fmtCoins(coins)}</span><Link to="/coins" className="btn btn--sm btn--primary" onClick={onClose}>เติมคอยน์</Link></div> : null}
      {err ? <div className="mt-16"><ErrorLine error={err} onNavigate={onClose} /></div> : null}
    </Modal>
  );
}

/** โน้ตความจำของแชท (AI ต้องจำ) — จำกัดตามระดับคุยคลับ */
export function NoteDialog({ open, chatId, memory, limit, onClose, onSaved }) {
  const [text, setText] = useState("");
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (open) { setText(memory?.text || ""); setActive(memory ? memory.isActive !== false : true); setErr(null); }
  }, [open, memory]);
  const over = limit > 0 && text.length > limit;
  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.chats.memory.save(chatId, text, active);
      onSaved(r.memory, r.limit);
      onClose();
    } catch (e) {
      setErr(errorInfo(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open={open} title="โน้ตตัวละคร" onClose={onClose}
      footer={<><Button onClick={onClose}>ยกเลิก</Button><Button variant="primary" disabled={busy || over} onClick={save}>บันทึก</Button></>}>
      <p className="muted mb-16" style={{ fontSize: 13 }}>สิ่งที่อยากให้ AI จำตลอดการคุยในแชทนี้ เช่น ชื่อเล่น เรื่องที่เล่าไปแล้ว หรือกติกาการเล่นบทบาท</p>
      <Input multiline rows={8} label="เนื้อหาโน้ต" value={text} onChange={(e) => setText(e.target.value)} placeholder="เช่น เรียกฉันว่า 'พี่มินต์' และอย่าลืมว่าเรากำลังเตรียมสอบ" error={over ? `เกินโควตา ${fmtCoins(text.length - limit)} ตัวอักษร` : ""} />
      <div className="row-between" style={{ marginTop: -6 }}>
        <span className={over ? "error-text" : "faint"} style={{ fontSize: 12 }}>{fmtCoins(text.length)} / {fmtCoins(limit)} ตัวอักษร</span>
        <Link to="/club" className="faint" style={{ fontSize: 12 }} onClick={onClose}>เพิ่มโควตาด้วยระดับคุยคลับ</Link>
      </div>
      <label className="check mt-16"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> ใช้โน้ตนี้กับ AI (กำลังใช้งาน)</label>
      {err ? <div className="mt-16"><ErrorLine error={err} onNavigate={onClose} /></div> : null}
    </Modal>
  );
}

/** คำ/ประโยคที่ไม่ต้องการให้ AI ใช้ */
export function BannedDialog({ open, words, onClose, onSave }) {
  const [list, setList] = useState([]);
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { if (open) { setList(words || []); setV(""); setErr(""); } }, [open, words]);
  const add = () => {
    const w = v.trim().slice(0, 60);
    if (!w) return;
    if (list.includes(w)) { setV(""); return; }
    if (list.length >= 50) { setErr("เพิ่มได้สูงสุด 50 คำ"); return; }
    setList([...list, w]);
    setV("");
    setErr("");
  };
  const save = async () => {
    setBusy(true);
    try { await onSave(list); onClose(); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal open={open} title="คำที่ไม่ต้องการ" onClose={onClose}
      footer={<><Button onClick={onClose}>ยกเลิก</Button><Button variant="primary" disabled={busy} onClick={save}>บันทึก</Button></>}>
      <p className="muted mb-16" style={{ fontSize: 13 }}>คำหรือประโยคที่อยากให้ AI เลี่ยงในแชทนี้ ({list.length}/50)</p>
      <div className="row" style={{ alignItems: "flex-start" }}>
        <Input className="grow" aria-label="เพิ่มคำที่ไม่ต้องการ" placeholder="พิมพ์คำแล้วกดเพิ่ม" value={v} maxLength={60} onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.nativeEvent.isComposing) { e.preventDefault(); add(); } }} />
        <Button onClick={add} disabled={!v.trim()} style={{ marginTop: 0 }}>เพิ่ม</Button>
      </div>
      {err ? <div className="error-text" role="alert">{err}</div> : null}
      <div className="chip-row mt-8" style={{ flexWrap: "wrap" }}>
        {list.map((w) => <Chip key={w} onClick={() => setList(list.filter((x) => x !== w))} aria-label={`ลบ ${w}`}>{w} <Icon name="x" size={13} /></Chip>)}
        {!list.length ? <span className="faint" style={{ fontSize: 13 }}>ยังไม่มีคำที่ห้าม</span> : null}
      </div>
    </Modal>
  );
}
